import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import {
  ArrowClockwise,
  ArrowCounterclockwise,
  Fullscreen,
  FullscreenExit,
  InfoCircleFill,
  Sliders,
} from 'react-bootstrap-icons';
import { Form, Formik } from 'formik';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import {
  selectScheduleCanRedo,
  selectScheduleCanUndo,
  selectScheduleMutationBlocked,
  selectScheduleSelectedCount,
  selectScheduleBulkAdjustEligible,
  selectScheduleDayOptions,
  selectScheduleTeamFilterOptions,
  selectScheduleCurrentFilters,
  selectUnscheduledMatches,
  selectScheduleLoadedSaved,
  selectScheduleItemScaleMultiplier,
  selectScheduleColumnWidth,
  selectScheduleTournamentInfo,
  selectScheduleEditorFullscreen,
} from '../../../../../features/schedule/scheduleSelectors';
import {
  setCurrentFilters,
  clearScheduleStates,
  setScheduleEditorFullscreen,
  undoScheduleChange,
  redoScheduleChange,
} from '../../../../../features/schedule/scheduleSlice';
import { CustomButton, ReactSelectForm, RoundedAvatar } from '../../../../../components';
import { BackIcon } from '../../../../../assets/icons/BackIcon';
import { TagIdsEnum } from '../../../../../constant/navigation';
import { ScheduleFilterType } from '../../../../../types/enums';
import {
  SCHEDULE_ZOOM_COLUMN_WIDTH_MAX,
  SCHEDULE_ZOOM_COLUMN_WIDTH_MIN,
  SCHEDULE_ZOOM_SCALE_MAX,
  SCHEDULE_ZOOM_SCALE_MIN,
  SCHEDULE_ZOOM_SCALE_STEP,
  type ScheduleZoomFloors,
} from '../../../../../features/schedule/scheduleZoomBounds';
import { ScheduleLayoutSettingsModal } from './ScheduleLayoutSettingsModal';
import { ScheduleBulkAdjustModal } from './ScheduleBulkAdjustModal';
import { ScheduleKeyboardShortcutsModal } from './ScheduleKeyboardShortcutsModal';
import { ScheduleExportActions } from './ScheduleExportActions';
import { SchedulePrintPreviewModal } from './SchedulePrintPreviewModal';
import { ScheduleToolbarButton } from './ScheduleToolbarButton';
import { ScheduleSavePublishActions } from './ScheduleSavePublishActions';
import { useScheduleExport } from './useScheduleExport';
import { SCHEDULE_ZOOM_COLUMN_WIDTH_STEP } from './scheduleLayoutHooks';

type FilterSelectValue = string | number;
type FilterFormikValues = {
  'filter-classes'?: FilterSelectValue[];
  'filter-teams'?: FilterSelectValue[];
};
type SelectOptionLabel = {
  label: string;
  logo?: string | null;
};

const getSelectOptionLabel = (data: unknown): SelectOptionLabel => {
  if (typeof data === 'object' && data !== null && 'label' in data) {
    const option = data as { label?: unknown; logo?: unknown };
    return {
      label: typeof option.label === 'string' ? option.label : '',
      logo: typeof option.logo === 'string' || option.logo === null ? option.logo : null,
    };
  }
  return { label: '' };
};

export type ScheduleToolbarProps = {
  selectedDayKey: number;
  onSelectedDayKeyChange: (dayKey: number) => void;
  scheduleZoomFloors: ScheduleZoomFloors;
  applyScheduleScale: (value: number) => void;
  applyScheduleColumnWidth: (value: number) => void;
  showConflictsToggleVisible: boolean;
  showScheduleConflicts: boolean;
  onShowScheduleConflictsChange: (value: boolean) => void;
  blockInvalidPlacement: boolean;
  onBlockInvalidPlacementChange: (value: boolean) => void;
  scheduleEditingBlocked: boolean;
};

export const ScheduleToolbar = memo(function ScheduleToolbar({
  selectedDayKey,
  onSelectedDayKeyChange,
  scheduleZoomFloors,
  applyScheduleScale,
  applyScheduleColumnWidth,
  showConflictsToggleVisible,
  showScheduleConflicts,
  onShowScheduleConflictsChange,
  blockInvalidPlacement,
  onBlockInvalidPlacementChange,
  scheduleEditingBlocked,
}: ScheduleToolbarProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const dispatch = useAppDispatch();

  const dayOptions = useAppSelector(selectScheduleDayOptions);
  const teamFilterOptions = useAppSelector(selectScheduleTeamFilterOptions);
  const currentFilters = useAppSelector(selectScheduleCurrentFilters);
  const unscheduledMatches = useAppSelector(selectUnscheduledMatches);
  const loadedSavedSchedule = useAppSelector(selectScheduleLoadedSaved);
  const scheduleItemScaleMultiplier = useAppSelector(selectScheduleItemScaleMultiplier);
  const scheduleColumnWidth = useAppSelector(selectScheduleColumnWidth);
  const tournamentInfo = useAppSelector(selectScheduleTournamentInfo);
  const scheduleEditorFullscreen = useAppSelector(selectScheduleEditorFullscreen);
  const canUndoSchedule = useAppSelector(selectScheduleCanUndo);
  const canRedoSchedule = useAppSelector(selectScheduleCanRedo);
  const scheduleMutationBlocked = useAppSelector(selectScheduleMutationBlocked);
  const selectedCount = useAppSelector(selectScheduleSelectedCount);
  const bulkAdjustEligible = useAppSelector(selectScheduleBulkAdjustEligible);
  const scheduleExport = useScheduleExport(selectedDayKey);
  const [bulkAdjustOpen, setBulkAdjustOpen] = useState(false);

  const updateFilterItems = useCallback(
    (formikValues: FilterFormikValues) => {
      const classes = formikValues['filter-classes'] || [];
      const teams = formikValues['filter-teams'] || [];
      const extractValue = (item: FilterSelectValue) => Number(item);
      const filteredClasses = classes.map((item) => ({
        type: ScheduleFilterType.CLASS,
        value: extractValue(item),
      }));
      const filteredTeams = teams.map((item) => ({
        type: ScheduleFilterType.TEAM,
        value: extractValue(item),
      }));
      dispatch(setCurrentFilters([...filteredClasses, ...filteredTeams]));
    },
    [dispatch]
  );

  const initialFormikValues = useMemo(
    () => ({
      'filter-classes': currentFilters
        .filter((filter) => filter.type === ScheduleFilterType.CLASS)
        .map((filter) => filter.value),
      'filter-teams': currentFilters
        .filter((filter) => filter.type === ScheduleFilterType.TEAM)
        .map((filter) => filter.value),
    }),
    [currentFilters]
  );

  const classFilterOptions = useMemo(
    () =>
      (tournamentInfo?.classes || []).map((c) => ({
        label: c.name,
        value: c.id,
      })),
    [tournamentInfo?.classes]
  );

  return (
    <header className="schedule-toolbar">
      <div className="schedule-toolbar__primary">
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          <CustomButton
            btnText=""
            variant="outline-dark"
            className="d-flex align-items-center gap-2 m-0"
            cb={() => dispatch(clearScheduleStates())}
            title={t(prefix + 'backToConstraintsHelper')}
          >
            <BackIcon w={'25'} h={'25'} />
            <span className="text-muted small mb-0 d-none d-sm-inline">
              {t(prefix + 'backToConstraints')}
            </span>
            <InfoCircleFill title={t(prefix + 'backToConstraintsHelper')} />
          </CustomButton>
        </div>

        <div
          className="schedule-toolbar__days btn-group flex-wrap"
          role="group"
          aria-label={t(prefix + 'scheduleToolbarDaysAria')}
        >
          {dayOptions.map((day) => (
            <button
              type="button"
              key={day.dayKey}
              className={`btn btn-sm ${
                selectedDayKey === day.dayKey ? 'btn-primary' : 'btn-outline-primary'
              }`}
              onClick={() => onSelectedDayKeyChange(day.dayKey)}
            >
              {t(prefix + 'scheduleSettingsDayLabel', { day: day.dayKey + 1 })}
            </button>
          ))}
        </div>

        <span className="badge bg-secondary schedule-toolbar__badge">
          {t(prefix + 'scheduleToolbarUnscheduledBadge', { count: unscheduledMatches.length })}
        </span>

        <div className="schedule-toolbar__actions ms-auto d-flex align-items-center gap-2 flex-shrink-0 flex-wrap justify-content-end">
          {scheduleEditorFullscreen ? <ScheduleSavePublishActions /> : null}
          {showConflictsToggleVisible ? (
            <ScheduleToolbarButton
              aria-pressed={showScheduleConflicts}
              aria-label={t(prefix + 'scheduleShowConflicts')}
              title={t(prefix + 'scheduleShowConflictsTooltip')}
              onClick={() => onShowScheduleConflictsChange(!showScheduleConflicts)}
              variant={showScheduleConflicts ? 'primary' : 'outline-secondary'}
              labelClassName="d-none d-lg-inline small"
            >
              {t(prefix + 'scheduleShowConflicts')}
            </ScheduleToolbarButton>
          ) : null}
          <ScheduleToolbarButton
            aria-pressed={blockInvalidPlacement}
            aria-label={t(prefix + 'scheduleBlockInvalidPlacement')}
            title={t(prefix + 'scheduleBlockInvalidPlacementTooltip')}
            disabled={scheduleEditingBlocked}
            onClick={() => onBlockInvalidPlacementChange(!blockInvalidPlacement)}
            variant={blockInvalidPlacement ? 'primary' : 'outline-secondary'}
            labelClassName="d-none d-lg-inline small"
          >
            {t(prefix + 'scheduleBlockInvalidPlacement')}
          </ScheduleToolbarButton>
          <ScheduleToolbarButton
            disabled={!bulkAdjustEligible || scheduleMutationBlocked || scheduleEditingBlocked}
            aria-label={t(prefix + 'scheduleBulkAdjustToolbar', 'Bulk adjust selection')}
            title={t(prefix + 'scheduleBulkAdjustToolbarTooltip', {
              count: selectedCount,
              defaultValue: 'Adjust time, courts, duration, or unschedule {{count}} selected matches',
            })}
            onClick={() => setBulkAdjustOpen(true)}
            icon={<Sliders size={18} aria-hidden />}
            labelClassName="d-none d-lg-inline small"
          >
            {t(prefix + 'scheduleBulkAdjustToolbar', 'Bulk adjust')}
            {selectedCount > 0 ? ` (${selectedCount})` : ''}
          </ScheduleToolbarButton>
          <ScheduleBulkAdjustModal show={bulkAdjustOpen} onClose={() => setBulkAdjustOpen(false)} />
          <ScheduleKeyboardShortcutsModal />
          <ScheduleToolbarButton
            disabled={!canUndoSchedule || scheduleMutationBlocked}
            aria-label={t(prefix + 'scheduleUndo')}
            title={t(prefix + 'scheduleUndoTooltip')}
            onClick={() => dispatch(undoScheduleChange())}
            icon={<ArrowCounterclockwise size={18} aria-hidden />}
            labelClassName="d-none d-lg-inline small"
          >
            {t(prefix + 'scheduleUndo')}
          </ScheduleToolbarButton>
          <ScheduleToolbarButton
            disabled={!canRedoSchedule || scheduleMutationBlocked}
            aria-label={t(prefix + 'scheduleRedo')}
            title={t(prefix + 'scheduleRedoTooltip')}
            onClick={() => dispatch(redoScheduleChange())}
            icon={<ArrowClockwise size={18} aria-hidden />}
            labelClassName="d-none d-lg-inline small"
          >
            {t(prefix + 'scheduleRedo')}
          </ScheduleToolbarButton>
          {loadedSavedSchedule && <ScheduleLayoutSettingsModal />}
          <div className="schedule-toolbar__export-desktop d-none d-lg-inline-flex align-items-center gap-2">
            <ScheduleExportActions variant="desktop" exportApi={scheduleExport} />
          </div>
          <div className="schedule-toolbar__zoom-desktop d-none d-lg-flex align-items-center gap-2">
            <div className="d-flex align-items-center gap-2 bg-white px-2 py-1 border rounded shadow-sm">
              <span
                className="fw-bold text-muted small"
                title={t(prefix + 'scheduleZoomHeightLabel')}
              >
                ↕
              </span>
              <input
                type="range"
                className="form-range"
                min={SCHEDULE_ZOOM_SCALE_MIN}
                max={SCHEDULE_ZOOM_SCALE_MAX}
                step={SCHEDULE_ZOOM_SCALE_STEP}
                value={scheduleItemScaleMultiplier}
                onChange={(e) => applyScheduleScale(Number(e.target.value))}
                style={{ width: 'min(200px, 28vw)', cursor: 'pointer' }}
                aria-label={t(prefix + 'scheduleZoomHeightLabel')}
              />
            </div>
            <div className="d-flex align-items-center gap-2 bg-white px-2 py-1 border rounded shadow-sm">
              <span
                className="fw-bold text-muted small"
                title={t(prefix + 'scheduleZoomWidthLabel')}
              >
                ↔
              </span>
              <input
                type="range"
                className="form-range"
                min={SCHEDULE_ZOOM_COLUMN_WIDTH_MIN}
                max={SCHEDULE_ZOOM_COLUMN_WIDTH_MAX}
                step={SCHEDULE_ZOOM_COLUMN_WIDTH_STEP}
                value={scheduleColumnWidth}
                onChange={(e) => applyScheduleColumnWidth(Number(e.target.value))}
                style={{ width: 'min(200px, 28vw)', cursor: 'pointer' }}
                aria-label={t(prefix + 'scheduleZoomWidthLabel')}
              />
            </div>
          </div>
          <div className="schedule-toolbar__zoom-mobile-wrap d-inline-flex align-items-center">
            <Dropdown className="schedule-toolbar__zoom-mobile d-lg-none" align="end">
              <Dropdown.Toggle
                as={ScheduleToolbarButton}
                id="schedule-zoom-dropdown"
                aria-label={t(prefix + 'scheduleMoreOptions')}
                title={t(prefix + 'scheduleMoreOptions')}
                icon={<Sliders size={18} aria-hidden />}
                labelClassName="d-none d-md-inline small"
              >
                {t(prefix + 'scheduleMoreOptions')}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Header>{t(prefix + 'scheduleExportSection')}</Dropdown.Header>
                <ScheduleExportActions variant="mobile" exportApi={scheduleExport} />
                <Dropdown.Divider />
                <Dropdown.Header>{t(prefix + 'scheduleZoomHeightLabel')}</Dropdown.Header>
                <Dropdown.ItemText as="div" className="px-3 py-2">
                  <input
                    type="range"
                    className="form-range"
                    min={SCHEDULE_ZOOM_SCALE_MIN}
                    max={SCHEDULE_ZOOM_SCALE_MAX}
                    step={SCHEDULE_ZOOM_SCALE_STEP}
                    value={scheduleItemScaleMultiplier}
                    onChange={(e) => applyScheduleScale(Number(e.target.value))}
                    aria-label={t(prefix + 'scheduleZoomHeightLabel')}
                  />
                </Dropdown.ItemText>
                <Dropdown.Header>{t(prefix + 'scheduleZoomWidthLabel')}</Dropdown.Header>
                <Dropdown.ItemText as="div" className="px-3 py-2">
                  <input
                    type="range"
                    className="form-range"
                    min={SCHEDULE_ZOOM_COLUMN_WIDTH_MIN}
                    max={SCHEDULE_ZOOM_COLUMN_WIDTH_MAX}
                    step={SCHEDULE_ZOOM_COLUMN_WIDTH_STEP}
                    value={scheduleColumnWidth}
                    onChange={(e) => applyScheduleColumnWidth(Number(e.target.value))}
                    aria-label={t(prefix + 'scheduleZoomWidthLabel')}
                  />
                </Dropdown.ItemText>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <ScheduleToolbarButton
            aria-pressed={scheduleEditorFullscreen}
            aria-label={
              scheduleEditorFullscreen
                ? t(prefix + 'scheduleFullscreenExit')
                : t(prefix + 'scheduleFullscreenEnter')
            }
            title={
              scheduleEditorFullscreen
                ? t(prefix + 'scheduleFullscreenExitTooltip')
                : t(prefix + 'scheduleFullscreenEnterTooltip')
            }
            onClick={() => dispatch(setScheduleEditorFullscreen(!scheduleEditorFullscreen))}
            icon={
              scheduleEditorFullscreen ? (
                <FullscreenExit size={18} aria-hidden />
              ) : (
                <Fullscreen size={18} aria-hidden />
              )
            }
            labelClassName="d-none d-md-inline small"
          >
            {scheduleEditorFullscreen
              ? t(prefix + 'scheduleFullscreenExit')
              : t(prefix + 'scheduleFullscreenEnter')}
          </ScheduleToolbarButton>
        </div>
      </div>

      <div className="schedule-toolbar__secondary">
        <Formik initialValues={initialFormikValues} onSubmit={updateFilterItems}>
          {({ submitForm, setFieldValue }) => (
            <Form autoComplete="off" id={TagIdsEnum.SCHEDULE}>
              <div className="row g-2 mb-0">
                <div className="col-md-6">
                  <ReactSelectForm
                    isMulti
                    name="filter-classes"
                    placeholder={t(prefix + 'scheduleFilterClassPlaceholder')}
                    options={classFilterOptions}
                    onChangeCb={(val: FilterSelectValue[]) => {
                      setFieldValue('filter-classes', val);
                      setTimeout(submitForm, 0);
                    }}
                    formatOptionLabel={(data: unknown) => {
                      const option = getSelectOptionLabel(data);
                      return <div className="d-flex align-items-center">{option.label}</div>;
                    }}
                  />
                </div>
                <div className="col-md-6">
                  <ReactSelectForm
                    isMulti
                    name="filter-teams"
                    placeholder={t(prefix + 'scheduleFilterTeamPlaceholder')}
                    options={teamFilterOptions}
                    onChangeCb={(val: FilterSelectValue[]) => {
                      setFieldValue('filter-teams', val);
                      setTimeout(submitForm, 0);
                    }}
                    formatOptionLabel={(data: unknown) => {
                      const option = getSelectOptionLabel(data);
                      return (
                        <div className="d-flex align-items-center">
                          <RoundedAvatar
                            avatar={option.logo || null}
                            alt="Team logo"
                            name="user"
                          />
                          <span className="ms-2">{option.label}</span>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {scheduleExport.printModalOpen ? (
        <SchedulePrintPreviewModal
          show={scheduleExport.printModalOpen}
          onClose={() => scheduleExport.setPrintModalOpen(false)}
          buildPrintModel={scheduleExport.buildPrintModel}
          courtNames={scheduleExport.printCourtNames}
          categoryOptions={scheduleExport.printCategoryOptions}
          documentTitle={scheduleExport.printDocumentTitle}
        />
      ) : null}
    </header>
  );
});
